import * as moment from 'moment-timezone';
import _ from 'lodash';

import { groupByFields } from '../utils/collection';
import { FULL_DATE_FORMAT } from '../constants/date';
import createDateRange from '../utils/date';
import { fromFilter, toFilter } from './filterConverter';

const emptyTypeExtractor = () => undefined;

export const fromCompound = (compound, typeExtractor = emptyTypeExtractor) => ({
  id: compound.id,
  name: compound.name,
  closingRange: {},
  dateRange:
    !!compound.query.filter && !!compound.query.filter.from ? { ...compound.query.filter } : null,
  steps: compound.query.steps.map((step) => ({
    filter: fromFilter(step.filter, (field) => typeExtractor(step.eventName, field)),
    closingRange: {
      value: moment.duration(step.closingRange).asMilliseconds(),
    },
    enabled: step.enabled,
    count: step.count,
    countOperator: step.countOperator,
    descriptionVisible: false,
    event: step.eventName,
    optional: step.optional,
    type: step.type !== 'DONE' ? 'LOCAL_NEGATIVE' : 'POSITIVE',
  })),
});

export const toCompound = (compound) => ({
  id: compound.id,
  name: compound.name,
  query: {
    filter: compound.dateRange ? { ...compound.dateRange } : null,
    steps: compound.steps.map((step, stepIndex) => ({
      enabled: step.enabled,
      eventName: step.event,
      count: step.count,
      countOperator: step.countOperator,
      closingRange: moment.duration((step.closingRange || {}).value).toISOString(),
      filter: toFilter(step.filter),
      optional: step.optional && stepIndex !== 0,
      type: step.type === 'LOCAL_NEGATIVE' ? 'NOT_DONE' : 'DONE',
    })),
  },
});

function getRange({ conversionInterval, globalFilter, timezone }) {
  const from = moment.tz(globalFilter.from, FULL_DATE_FORMAT, timezone);
  const to = moment.tz(globalFilter.to, FULL_DATE_FORMAT, timezone).subtract(1, 'day');
  const moments = createDateRange(from, to, conversionInterval.toLowerCase());
  const dates = [];
  for (let i = 0; i < moments.length; i++) {
    dates.push(moments[i].valueOf());
  }
  return dates;
}

function rowsToObj(rows) {
  const obj = {};
  for (let i = 0; i < rows.length; i++) {
    obj[rows[i].date] = rows[i];
  }
  return obj;
}

function fillEmptyDates({ key, data }, dates) {
  const rowsObj = rowsToObj(data);
  const rows = [];
  for (let i = 0; i < dates.length; i++) {
    rows.push(rowsObj[dates[i]] || { ...key, date: dates[i], value: 0 });
  }
  return {
    key,
    data: rows,
  };
}

function flatSteps(stepsByDate, timezone) {
  const stepsByDateEntries = Object.entries(stepsByDate);
  const rows = [];
  for (let rowIndex = 0; rowIndex < stepsByDateEntries.length; rowIndex++) {
    const steps = stepsByDateEntries[rowIndex][1];
    for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
      const stepEntries = Object.entries(steps[stepIndex]);
      for (let i = 0; i < stepEntries.length; i++) {
        rows.push({
          date: moment.tz(stepsByDateEntries[rowIndex][0], 'YYYY-MM-DD', timezone).valueOf(),
          step: stepIndex,
          value: stepEntries[i][1],
          optional: stepEntries[i][0] === 'optionalValue',
        });
      }
    }
  }

  return rows;
}

function toSeriesData(step, firstStep) {
  const seriesData = [];
  for (let i = 0; i < step.length; i++) {
    const first = firstStep[i].value;
    const { value } = step[i];
    const y = first !== 0 ? _.round((value / first) * 100, 2) : 0;
    seriesData.push({
      x: step[i].date,
      y,
      name: `${y}% (${value}/${first})`,
    });
  }
  return seriesData;
}

const toName = ({ step, optional }, funnel) => {
  const steps = funnel.steps.filter((stepItem) => stepItem.enabled);
  return `1. ${steps[0].event} -> ${step + 1}. ${steps[step].event} ${
    optional ? '(optional)' : ''
  }`;
};

function toChartData({ stepsByDate }, query, funnel) {
  const rows = flatSteps(stepsByDate, query.timezone);

  if (rows.length === 0) {
    return undefined;
  }

  const dates = getRange(query);
  const steps = groupByFields(rows, 'step', 'optional').map((s) => fillEmptyDates(s, dates));

  if (steps.length < 2) {
    return undefined;
  }

  const firstStep = steps.find((s) => s.key.step === 0);

  const conversions = steps
    .filter((s) => s.key.step !== 0)
    .map((step, index) => ({
      id: index,
      name: toName(step.key, funnel),
      data: toSeriesData(step.data, firstStep.data),
    }));

  return {
    name: funnel.name,
    id: '',
    series: conversions,
    from: dates[0],
    to: dates[dates.length - 1],
  };
}

function getConversion(completed, started) {
  return started === 0 ? 0 : ((completed * 100) / started).toFixed(1);
}

export const fromFunnelResult = (result, query, funnel, unit = undefined) => {
  const started = result.steps[0].value;
  const completed = ([...result.steps].reverse().find(({ value }) => value !== undefined) || {})
    .value;
  const optionalCompleted = (
    [...result.steps].reverse().find(({ optionalValue }) => optionalValue !== undefined) || {}
  ).optionalValue;
  const chart = toChartData(result, query, funnel);

  const totalResult = {
    started,
    completed,
    optionalCompleted,
    conversion: getConversion(completed, started),
    optionalConversion:
      optionalCompleted !== undefined ? getConversion(optionalCompleted, started) : undefined,
    steps: result.steps.map((e) => ({ count: e.value, optionalCount: e.optionalValue })),
    chart,
  };

  if (unit !== undefined) {
    totalResult.unit = unit;
  }

  return totalResult;
};
